import React, { useState } from "react";
import "./PositionsSection.css";
import { supabase } from "../../supabaseClient";
import { sendApplicationNotification } from "../../utils/applicationNotifications";

const PositionsSection = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    message: "",
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const [positions] = useState([
    {
      id: 1,
      title: "Freelance Cook",
      type: "Full-time/Part-time",
      location: "Flexible Location",
      posted: "2 days ago",
      tags: ["Remote"],
      description:
        "We're looking for passionate cooks to create amazing dishes for our clients. Showcase your culinary skills and build your reputation.",
      skills: [
        "Vegetarian",
        "Non-Vegetarian",
        "Street Food",
        "Chinese",
        "Regional Cuisine",
      ],
      category: "culinary",
      department: "Culinary",
    },
    {
      id: 2,
      title: "General Worker",
      type: "Full-time/Part-time",
      location: "Flexible Location",
      posted: "2 days ago",
      tags: ["Remote"],
      description:
        "We're looking for dedicated general workers to support various operations. Bring your energy and willingness to learn.",
      skills: ["Manual Work", "Teamwork", "Basic Maintenance", "Organization"],
      category: "general",
      department: "Operations",
    },
  ]);

  const filters = [
    { key: "all", label: "All" },
    { key: "culinary", label: "Culinary" },
    { key: "general", label: "General" },
    { key: "management", label: "Management" },
    { key: "education", label: "Education" },
  ];

  // Application Modal Handlers
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // Upload file to Supabase Storage with multiple bucket fallbacks
  const uploadResume = async (file) => {
    try {
      // console.log("📄 Attempting to upload resume...", file.name);

      // Try different bucket names - use the same one as your HiringPage component
      const bucketNames = ["resumes", "applications", "documents"];
      
      for (const bucketName of bucketNames) {
        try {
          const fileExt = file.name.split(".").pop();
          const fileName = `${Date.now()}-${Math.random()
            .toString(36)
            .substring(2)}.${fileExt}`;
          const filePath = `resumes/${fileName}`;

          // console.log(`🔄 Trying bucket "${bucketName}"...`, filePath);

          const { data, error } = await supabase.storage
            .from(bucketName)
            .upload(filePath, file);

          if (error) {
            console.warn(`⚠️ Bucket "${bucketName}" failed:`, error.message);
            continue; // Try next bucket
          }

          // Get public URL
          const { data: urlData } = await supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath);

          if (!urlData || !urlData.publicUrl) {
            throw new Error("Could not get public URL for uploaded file");
          }

          // console.log(`✅ Resume uploaded to bucket "${bucketName}":`, urlData.publicUrl);
          return urlData.publicUrl;

        } catch (bucketError) {
          // console.warn(`❌ Bucket "${bucketName}" failed:`, bucketError.message);
          // Continue to next bucket
        }
      }

      // If all buckets fail, throw error
      throw new Error("No working storage bucket found. Please check Supabase storage configuration.");

    } catch (error) {
      // console.error("❌ All resume upload attempts failed:", error);
      throw new Error(`Unable to upload resume: ${error.message}`);
    }
  };

  // EmailJS notification function
  // Submit application to Supabase
  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // console.log("📝 Starting application submission...");

    try {
      let resumeUrl = null;

      // Upload resume if provided
      if (formData.resume) {
        setIsUploading(true);
        // console.log("📄 Processing resume...");
        
        // Validate file size
        if (formData.resume.size > 5 * 1024 * 1024) {
          throw new Error("File size too large. Please upload a file smaller than 5MB.");
        }

        // Validate file type
        const allowedTypes = ['.pdf', '.doc', '.docx'];
        const fileExt = '.' + formData.resume.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(fileExt)) {
          throw new Error("Invalid file type. Please upload PDF, DOC, or DOCX files only.");
        }

        resumeUrl = await uploadResume(formData.resume);
        setIsUploading(false);
        // console.log("📄 Resume URL obtained:", resumeUrl);
      }

      // console.log("💾 Saving to database...");
      const applicationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: selectedPosition.title,
        experience: formData.experience,
        message: formData.message,
        resume_url: resumeUrl,
        status: "new",
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("applications")
        .insert([applicationData])
        .select();

      if (error) {
        console.error("❌ Database error:", error);
        throw new Error(`Unable to save application: ${error.message}`);
      }

      // console.log("✅ Database save successful:", data);

      const notificationSent = await sendApplicationNotification({
        applicationId: data?.[0]?.id,
        applicantName: formData.name,
        applicantEmail: formData.email,
        positionName: selectedPosition?.title || applicationData.position,
        resumeUrl,
        phone: formData.phone,
        experience: formData.experience,
        message: formData.message,
      });

      if (!notificationSent) {
        console.warn("⚠️ Application email notification failed.");
      }

      setSubmitStatus({
        type: "success",
        message: `Thank you for applying to the "${selectedPosition.title}" position! We'll review your application and get back to you soon.`,
      });

      // console.log("🎉 Application submitted successfully!");

      setTimeout(() => {
        handleCloseModal();
      }, 3000);
    } catch (error) {
      console.error("❌ Submission error:", error);
      setSubmitStatus({
        type: "error",
        message: error.message || "Sorry, there was an error submitting your application. Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
    }
  };

  const handleApplyClick = (position) => {
    setSelectedPosition(position);
    setShowApplicationModal(true);
    setSubmitStatus(null);
  };

  const handleCloseModal = () => {
    setShowApplicationModal(false);
    setSelectedPosition(null);
    setSubmitStatus(null);
    // Reset form data
    setFormData({
      name: "",
      email: "",
      phone: "",
      experience: "",
      message: "",
      resume: null,
    });
    setIsSubmitting(false);
    setIsUploading(false);
  };

  const filteredPositions =
    activeFilter === "all"
      ? positions
      : positions.filter((position) => position.category === activeFilter);

  const handleFilterClick = (filterKey) => {
    setActiveFilter(filterKey);
  };

  const getTagClassName = (tag) => {
    const tagClassMap = {
      Remote: "tag-remote",
      "On-site": "tag-onsite",
      Manager: "tag-manager",
      Education: "tag-education",
    };
    return tagClassMap[tag] || "tag-default";
  };

  return (
    <>
      {/* Positions Section */}
      <div className="positions-section">
        <div className="section-header">
          <h3>Open Positions</h3>
          <p>
            Ready to take the next step in your career? Explore our current
            opportunities.
          </p>
        </div>

        <div className="filters">
          {filters.map((filter) => (
            <button
              key={filter.key}
              className={`filter-btn ${
                activeFilter === filter.key ? "active" : ""
              }`}
              onClick={() => handleFilterClick(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="positions-grid">
          {filteredPositions.length > 0 ? (
            filteredPositions.map((position) => (
              <PositionCard
                key={position.id}
                position={position}
                onApplyClick={() => handleApplyClick(position)}
                getTagClassName={getTagClassName}
              />
            ))
          ) : (
            <div className="no-positions">
              <i className="fas fa-search"></i>
              <h4>No positions found</h4>
              <p>There are no open positions matching your selected filter.</p>
            </div>
          )}
        </div>
      </div>

      {/* Application Modal */}
      {showApplicationModal && selectedPosition && (
        <div className="application-modal__overlay" onClick={handleCloseModal}>
          <div
            className="application-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="application-modal__header">
              <h2 className="application-modal__title">
                Apply for {selectedPosition.title}
              </h2>
              <p className="application-modal__subtitle">
                Join our team and grow with us
              </p>
              <button
                className="application-modal__close"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                <span className="application-modal__close-icon">×</span>
              </button>
            </div>

            <div className="application-modal__body">
              {/* Submit Status Messages */}
              {submitStatus && (
                <div
                  className={`application-modal__status application-modal__status--${submitStatus.type}`}
                >
                  <i
                    className={`fas ${
                      submitStatus.type === "success"
                        ? "fa-check-circle"
                        : "fa-exclamation-circle"
                    }`}
                  ></i>
                  {submitStatus.message}
                </div>
              )}

              {/* Uploading Status */}
              {isUploading && (
                <div className="application-modal__status application-modal__status--info">
                  <i className="fas fa-spinner fa-spin"></i>
                  Uploading resume...
                </div>
              )}

              {/* Position Information */}
              <div className="application-modal__position-info">
                <div className="application-modal__position-badge">
                  <i className="fas fa-briefcase"></i>
                  {selectedPosition.department || "General"}
                </div>
                <div className="application-modal__position-details">
                  <div className="application-modal__detail-item">
                    <span className="application-modal__detail-label">
                      Type
                    </span>
                    <span className="application-modal__detail-value">
                      {selectedPosition.type}
                    </span>
                  </div>
                  <div className="application-modal__detail-item">
                    <span className="application-modal__detail-label">
                      Location
                    </span>
                    <span className="application-modal__detail-value">
                      {selectedPosition.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Application Form */}
              <form
                onSubmit={handleSubmitApplication}
                className="application-modal__form"
                noValidate
              >
                <div className="application-modal__form-section">
                  <h3 className="application-modal__section-title">
                    Personal Information
                  </h3>

                  <div className="application-modal__form-row">
                    <div className="application-modal__form-group">
                      <label
                        htmlFor="name"
                        className="application-modal__form-label application-modal__form-label--required"
                      >
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="application-modal__form-input"
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div className="application-modal__form-group">
                      <label
                        htmlFor="email"
                        className="application-modal__form-label application-modal__form-label--required"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="application-modal__form-input"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>

                  <div className="application-modal__form-row">
                    <div className="application-modal__form-group">
                      <label
                        htmlFor="phone"
                        className="application-modal__form-label"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="application-modal__form-input"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div className="application-modal__form-group">
                      <label
                        htmlFor="experience"
                        className="application-modal__form-label"
                      >
                        Years of Experience
                      </label>
                      <input
                        type="text"
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="application-modal__form-input"
                        placeholder="e.g., 3 years"
                      />
                    </div>
                  </div>
                </div>

                <div className="application-modal__form-section">
                  <h3 className="application-modal__section-title">
                    Application Materials
                  </h3>

                  <div className="application-modal__form-group">
                    <label
                      htmlFor="resume"
                      className="application-modal__form-label"
                    >
                      Upload Resume (Optional)
                    </label>
                    <input
                      type="file"
                      id="resume"
                      name="resume"
                      onChange={handleInputChange}
                      accept=".pdf,.doc,.docx"
                      disabled={isSubmitting}
                      className="application-modal__form-file"
                    />
                    <div className="application-modal__form-help">
                      <i className="fas fa-info-circle"></i>
                      Accepted formats: PDF, DOC, DOCX (Max: 5MB)
                    </div>
                    {formData.resume && (
                      <div className="application-modal__file-selected">
                        <i className="fas fa-check-circle"></i>
                        Selected: {formData.resume.name}
                      </div>
                    )}
                  </div>

                  <div className="application-modal__form-group">
                    <label
                      htmlFor="message"
                      className="application-modal__form-label"
                    >
                      Cover Letter / Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      className="application-modal__form-textarea"
                      placeholder="Tell us why you're the perfect fit for this position and what excites you about this opportunity..."
                    />
                    <div className="application-modal__form-help">
                      <i className="fas fa-lightbulb"></i>
                      This is your chance to stand out from other applicants
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="application-modal__form-actions">
                  <button
                    type="button"
                    className="application-modal__btn application-modal__btn--secondary"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                  >
                    <i className="fas fa-times"></i>
                    {isSubmitting ? "Closing..." : "Cancel"}
                  </button>
                  <button
                    type="submit"
                    className="application-modal__btn application-modal__btn--primary"
                    disabled={isSubmitting || isUploading}
                  >
                    {isSubmitting || isUploading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        {isUploading ? "Uploading..." : "Submitting..."}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Submit Application
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// PositionCard Component
const PositionCard = ({ position, onApplyClick, getTagClassName }) => {
  const { title, type, location, posted, tags, description, skills } = position;

  return (
    <div className="position-card">
      <div className="position-header">
        <h4>{title}</h4>
        <div className="position-tags">
          {tags.map((tag, index) => (
            <span
              key={index}
              className={`position-tag ${getTagClassName(tag)}`}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="position-details">
        <span>
          <i className="fas fa-briefcase"></i> {type}
        </span>
        <span>
          <i className="fas fa-map-marker-alt"></i> {location}
        </span>
        <span>
          <i className="fas fa-clock"></i> Posted {posted}
        </span>
      </div>

      <p className="position-description">{description}</p>

      <div className="position-skills">
        {skills.map((skill, index) => (
          <span key={index} className="skill-tag">
            {skill}
          </span>
        ))}
      </div>

      <button onClick={onApplyClick} className="btn btn-primary apply-btn">
        <i className="fas fa-paper-plane"></i>
        Apply Now
      </button>
    </div>
  );
};

export default PositionsSection;
